import { useCallback, useEffect, useState } from "react";
import { useFieldApi } from "../../../hooks/useFieldApi";

/**
 * FS-01-03 아바타 — 카탈로그 프리셋(피코 친구)만(SR-SOC-03 · SR-CAT-01). 자유 프로필 필드 0(DR-02).
 * 와이어 순서: 🐢 🦊 🐘 🤖 🐝 🐳. 이름은 카탈로그(student.avatar.names.*).
 */
export const AVATARS = [
  { key: "turtle", emoji: "🐢" },
  { key: "fox", emoji: "🦊" },
  { key: "elephant", emoji: "🐘" },
  { key: "robot", emoji: "🤖" },
  { key: "bee", emoji: "🐝" },
  { key: "whale", emoji: "🐳" },
] as const;

export type AvatarKey = (typeof AVATARS)[number]["key"];
export const DEFAULT_AVATAR: AvatarKey = "turtle";

export const isAvatarKey = (k: unknown): k is AvatarKey =>
  typeof k === "string" && AVATARS.some((a) => a.key === k);

export const avatarEmoji = (k: AvatarKey) => AVATARS.find((a) => a.key === k)?.emoji ?? "🐢";

/**
 * 내 아바타 — 제어 평면 GET/PUT /api/courses/{c}/me/avatar {avatarKey}(S4 — 코스 단위 저장, SR-SOC-03)가 원본이다.
 * S3 의 '세션 동안만 기억' 대체 경로는 없앴다. 저장이 실패하면 화면은 이전 값으로 돌아가고 false 를 돌려준다.
 */
export function useMyAvatar() {
  const api = useFieldApi();
  const [avatar, setAvatar] = useState<AvatarKey>(DEFAULT_AVATAR);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    void api.myAvatar().then((k) => {
      if (!cancelled && isAvatarKey(k)) setAvatar(k);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const save = useCallback(
    async (k: AvatarKey): Promise<boolean> => {
      const before = avatar;
      setAvatar(k);
      const ok = api ? await api.saveAvatar(k) : false;
      if (!ok) setAvatar(before);
      return ok;
    },
    [api, avatar],
  );

  return { avatar, save };
}
